import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import ExerciseCard from '../shared/ExerciseCard';
import TranslatableText from '../shared/TranslatableText';

const FillInPanel = ({
  question,
  currentQuestionIndex,
  totalQuestions,
  wordOptions,
  droppedWord,
  feedback,
  score,
  isRevealed,
  showRevealButton,
  explanation,
  explanationLoading,
  onWordClick,
  onReveal,
  onNext,
  topic,
}) => {
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  // Enter advances to the next question once the current one is answered
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (feedback !== null) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [feedback, onNext]);

  // Split the partial answer on the blank placeholder
  const parts = question.partial_answer.split('____');

  return (
    <ExerciseCard>
      {/* Header with progress and score */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">
          Otazka {currentQuestionIndex + 1} z {totalQuestions}
        </h2>
        <div className="text-sm text-gray-700">
          Skore: <span className="font-semibold text-green-600">{score}</span> / {totalQuestions}
        </div>
      </div>

      {/* Question text */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
        <div className="text-gray-500 mb-2 font-semibold text-sm">
          Otazka:
        </div>
        <div className="text-lg text-gray-800 font-semibold">
          <TranslatableText text={question.question} textColor="#1f2937" topic={topic} />
        </div>
      </div>

      {/* Partial answer with blank */}
      <div className="bg-gray-50 p-4 rounded-xl mb-4">
        <div className="text-gray-500 mb-3 font-semibold text-sm">
          Doplňte odpověd:
        </div>
        <div className="flex items-center gap-2 flex-wrap p-3 bg-white rounded-lg border-2 border-gray-200">
          <TranslatableText text={parts[0]} topic={topic} />

          {/* Answer display */}
          <span
            className={`inline-block px-4 py-2 rounded-lg text-center min-w-[200px] font-semibold ${
              droppedWord
                ? feedback === 'correct'
                  ? 'bg-green-100 text-green-700 border-2 border-green-400'
                  : isRevealed
                    ? 'bg-blue-50 text-blue-700 border-2 border-blue-300'
                    : 'bg-red-100 text-red-600 border-2 border-red-400'
                : 'bg-gray-100 text-gray-400 border-2 border-dashed border-blue-400'
            }`}
          >
            {droppedWord || '________'}
          </span>

          {parts[1] && <TranslatableText text={parts[1]} topic={topic} />}
        </div>
      </div>

      {/* Feedback banner */}
      {feedback && !isRevealed && (
        <div className={`mb-4 p-3 rounded-lg text-center font-medium ${
          feedback === 'correct'
            ? 'bg-green-100 text-green-700 border border-green-300'
            : 'bg-red-100 text-red-600 border border-red-300'
        }`}>
          {feedback === 'correct' ? '✓ Správně!' : (
            <>✗ Špatně – správně je <strong>{question.answer}</strong></>
          )}
        </div>
      )}
      {isRevealed && (
        <div className="mb-4 p-3 rounded-lg text-center font-medium bg-blue-50 text-blue-700 border border-blue-200">
          Odpověď: <strong>{question.answer}</strong>
        </div>
      )}

      {/* Word options - clickable */}
      <div className="bg-gray-50 p-4 rounded-xl mb-4">
        <div className="text-gray-500 mb-3 font-semibold text-sm">
          Vyberte správné slovo a doplňte ho do věty:
        </div>
        <div className="flex gap-3 justify-center flex-wrap">
          {wordOptions.map((word, index) => (
            <button
              key={index}
              onClick={() => onWordClick(word)}
              disabled={feedback !== null}
              className={`px-6 py-3 rounded-lg text-lg font-semibold transition-all ${
                feedback === null
                  ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
              }`}
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {/* Nevím button */}
      {showRevealButton && feedback === null && (
        <div className="flex justify-center mb-4">
          <button
            onClick={onReveal}
            className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-lg transition-all"
          >
            Nevím
          </button>
        </div>
      )}

      {/* AI vysvětlení */}
      {feedback === 'incorrect' && (explanationLoading || explanation) && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm font-medium text-amber-700 mb-2">Vysvětlení</p>
          {explanation ? (
            <div className="text-gray-700 leading-relaxed prose prose-sm max-w-none">
              <ReactMarkdown>{explanation}</ReactMarkdown>
            </div>
          ) : (
            <div className="animate-pulse space-y-2">
              <div className="h-4 bg-amber-200/60 rounded w-full" />
              <div className="h-4 bg-amber-200/60 rounded w-5/6" />
              <div className="h-4 bg-amber-200/60 rounded w-4/6" />
            </div>
          )}
        </div>
      )}

      {/* Next / Results button */}
      {feedback !== null && (
        <div className="flex justify-center">
          <button
            onClick={onNext}
            className="py-3 px-6 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-all"
          >
            {isLastQuestion ? 'Zobrazit výsledky →' : 'Další →'}
          </button>
        </div>
      )}
    </ExerciseCard>
  );
};

export default FillInPanel;
